import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Database, AlertOctagon, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPES = [
  { type: "sales_category", label: "Sales Categories" },
  { type: "sales_channel", label: "Sales Channels" },
  { type: "expense_category", label: "Expense Categories" },
  { type: "inventory_category", label: "Inventory Categories" },
  { type: "unit", label: "Units of Measure" },
  { type: "payment_method", label: "Payment Methods" },
  { type: "department", label: "Departments" },
  { type: "wastage_reason", label: "Wastage Reasons" },
  { type: "adjustment_reason", label: "Adjustment Reasons" },
  { type: "supplier_category", label: "Supplier Categories" },
];

export default function Settings() {
  const { can, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { reloadMeta } = useMeta();
  const [defaultTheme, setDefaultTheme] = useState("system");
  const [active, setActive] = useState(TYPES[0].type);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/settings/masters", { params: { type: active } }); setValues(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [active]);

  useEffect(() => {
    api.get("/settings/app-config").then(({ data }) => setDefaultTheme(data.default_theme || "system")).catch(() => {});
  }, []);

  const saveDefaultTheme = async (t) => {
    setDefaultTheme(t);
    try { await api.put("/settings/app-config", { default_theme: t }); toast.success("Default theme updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const THEME_OPTS = [["light", "Light", Sun], ["dark", "Dark", Moon], ["system", "System", Monitor]];

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/settings/masters/${editing.id}`, { name });
      else await api.post("/settings/masters", { type: active, name });
      toast.success("Saved"); setOpen(false); setName(""); setEditing(null); load(); reloadMeta();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/settings/masters/${delTarget.id}`); toast.success("Deleted"); setDelTarget(null); load(); reloadMeta(); }
    catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };
  const clearDemo = async () => {
    try { await api.post("/admin/clear-demo"); toast.success("Demo data cleared"); setConfirmClear(false); reloadMeta(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reseed = async () => {
    try { await api.post("/admin/seed", { force: true }); toast.success("Demo data reseeded"); reloadMeta(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Master data drives every dropdown across Hunter. Changes reflect instantly." />

      <div className="rounded-sm border border-border bg-card p-5" data-testid="appearance-card">
        <h3 className="font-display font-bold">Appearance</h3>
        <p className="mt-1 text-sm text-muted-foreground">Choose your personal theme. It persists across devices on your account.</p>
        <div className="mt-4 space-y-4">
          <div>
            <div className="eyebrow mb-2">Your theme</div>
            <div className="flex flex-wrap gap-2">
              {THEME_OPTS.map(([val, label, Icon]) => (
                <button key={val} onClick={() => setTheme(val)} data-testid={`pref-theme-${val}`}
                  className={cn("flex items-center gap-2 rounded-sm border px-4 py-2 text-sm transition-colors",
                    theme === val ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary")}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </div>
          </div>
          {user?.role === "super_admin" && (
            <div className="border-t border-border pt-4">
              <div className="eyebrow mb-2">Chain default theme (new users)</div>
              <div className="flex flex-wrap gap-2">
                {THEME_OPTS.map(([val, label, Icon]) => (
                  <button key={val} onClick={() => saveDefaultTheme(val)} data-testid={`default-theme-${val}`}
                    className={cn("flex items-center gap-2 rounded-sm border px-4 py-2 text-sm transition-colors",
                      defaultTheme === val ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary")}>
                    <Icon className="h-4 w-4" />{label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        <div className="space-y-1">
          {TYPES.map((t) => (
            <button key={t.type} onClick={() => setActive(t.type)} data-testid={`master-tab-${t.type}`}
              className={cn("flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors", active === t.type ? "bg-sidebar text-white" : "hover:bg-secondary")}>
              <Database className="h-4 w-4 shrink-0 opacity-70" />{t.label}
            </button>
          ))}
        </div>

        <div className="rounded-sm border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border p-4">
            <h3 className="font-display font-bold">{TYPES.find((t) => t.type === active)?.label}</h3>
            {can("settings:create") && <Button size="sm" onClick={() => { setEditing(null); setName(""); setOpen(true); }} data-testid="add-master-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>}
          </div>
          <div className="divide-y divide-border">
            {loading ? <p className="p-6 text-sm text-muted-foreground">Loading…</p> : values.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No values yet.</p> : values.map((v) => (
              <div key={v.id} data-testid={`master-${v.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/40">
                <div className="flex items-center gap-3"><span className="font-medium">{v.name}</span><StatusBadge status={v.active === false ? "inactive" : "active"} /></div>
                <div className="flex items-center gap-1">
                  {can("settings:edit") && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(v); setName(v.name); setOpen(true); }} data-testid={`edit-master-${v.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {can("settings:delete") && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelTarget(v)} data-testid={`del-master-${v.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {user?.role === "super_admin" && (
        <div className="rounded-sm border border-destructive/30 bg-destructive/[0.03] p-5">
          <div className="flex items-center gap-2 text-destructive"><AlertOctagon className="h-5 w-5" /><h3 className="font-display font-bold">Danger Zone</h3></div>
          <p className="mt-1 text-sm text-muted-foreground">Reseed regenerates demo data. Clear removes all demo/transactional data before going to production (keeps your owner account).</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={reseed} data-testid="reseed-btn">Reseed demo data</Button>
            <Button variant="destructive" onClick={() => setConfirmClear(true)} data-testid="clear-demo-btn">Clear demo data</Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="master-form">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit value" : "Add value"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label className="eyebrow">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="master-name" autoFocus /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" data-testid="master-submit">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)} title="Delete this value?" description="It will no longer appear in dropdowns. Existing records keep their value." confirmText="Delete" destructive onConfirm={remove} />
      <ConfirmDialog open={confirmClear} onOpenChange={setConfirmClear} title="Clear all demo data?" description="This permanently removes all outlets, sales, inventory and transactions. Only your owner account survives. Use before production launch." confirmText="Clear everything" destructive onConfirm={clearDemo} />
    </div>
  );
}
